'use client';

import { useMemo, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import FormProgressIndicator from './FormProgressIndicator';
import CompanyInfoStep from './CompanyInfoStep';
import ProjectDetailsStep from './ProjectDetailsStep';
import RequirementsStep from './RequirementsStep';
import DocumentUploadStep from './DocumentUploadStep';
import ReviewSubmitStep from './ReviewSubmitStep';

type CustomPlanForm = {
  companyName: string;
  companySize: string;
  industry: string;
  contactName: string;
  email: string;
  phone: string;
  projectType: string;
  projectTimeline: string;
  budget: string;
  projectDescription: string;
  features: string[];
  integrations: string[];
  compliance: string[];
  additionalNotes: string;
  uploadedFiles: File[];
  preferredContactMethod: string;
  consultationDate: string;
  consultationTime: string;
};

const CustomPlanInteractive = () => {
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<CustomPlanForm>({
    companyName: '',
    companySize: '',
    industry: '',
    contactName: '',
    email: '',
    phone: '',
    projectType: '',
    projectTimeline: '',
    budget: '',
    projectDescription: '',
    features: [],
    integrations: [],
    compliance: [],
    additionalNotes: '',
    uploadedFiles: [],
    preferredContactMethod: '',
    consultationDate: '',
    consultationTime: '',
  });
  const [errors] = useState<Record<string, string>>({});
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const steps = useMemo(
    () => [
      { number: 1, title: t('custom_plan.step1') ?? 'Company Info' },
      { number: 2, title: t('custom_plan.step2') ?? 'Project Details' },
      { number: 3, title: t('custom_plan.step3') ?? 'Requirements' },
      { number: 4, title: t('custom_plan.step4') ?? 'Documents' },
      { number: 5, title: t('custom_plan.step5') ?? 'Review' },
    ],
    [t]
  );

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleInputChange = (field: keyof CustomPlanForm, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field: keyof Pick<CustomPlanForm, 'features' | 'integrations' | 'compliance'>, value: string, checked: boolean) => {
    setFormData((prev) => {
      const currentValues = (prev[field] as string[]) || [];
      const updatedValues = checked
        ? [...currentValues, value]
        : currentValues.filter((item) => item !== value);
      return { ...prev, [field]: updatedValues };
    });
  };

  const handleFileUpload = (files: FileList) => {
    setFormData((prev) => ({
      ...prev,
      uploadedFiles: [...prev.uploadedFiles, ...Array.from(files)],
    }));
  };

  const handleFileRemove = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      uploadedFiles: prev.uploadedFiles.filter((_, i) => i !== index),
    }));
  };

  const estimatedBudgetLabel = useMemo(() => {
    const map: Record<string, string> = {
      small: '$25,000 - $50,000',
      medium: '$50,000 - $100,000',
      large: '$100,000 - $250,000',
      enterprise: '$250,000+',
      discuss: 'To be discussed',
      '': 'Pending selection',
    };
    return map[formData.budget] ?? 'Pending selection';
  }, [formData.budget]);

  const estimatedTimelineLabel = useMemo(() => {
    const map: Record<string, string> = {
      urgent: '1-2 months',
      short: '3-6 months',
      medium: '6-12 months',
      long: '12+ months',
      flexible: 'Flexible',
      '': 'Pending selection',
    };
    return map[formData.projectTimeline] ?? 'Pending selection';
  }, [formData.projectTimeline]);

  const handleSubmit = async () => {
    setSubmitStatus('submitting');
    setSubmitMessage('');
    try {
      const res = await fetch('/api/send-plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formData }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'Failed to send');
      }

      const data = await res.json();
      setSubmitStatus('success');
      setSubmitMessage(
        data?.skipped
          ? 'We received your details. (Note: email service not configured—set SMTP to receive messages.)'
          : 'Thanks! Your submission was sent. We will get back to you soon.'
      );
    } catch (err: any) {
      setSubmitStatus('error');
      setSubmitMessage(err?.message || 'Došlo je do greške prilikom slanja. Pokušajte ponovo.');
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <CompanyInfoStep data-component-id="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx:167:10" data-component-path="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx" data-component-line="167" data-component-file="CustomPlanInteractive.tsx" data-component-name="CompanyInfoStep" data-component-content="%7B%22elementName%22%3A%22CompanyInfoStep%22%7D"
            formData={formData}
            errors={errors}
            onInputChange={(field, value) => handleInputChange(field as keyof CustomPlanForm, value)}
          />
        );
      case 2:
        return (
          <ProjectDetailsStep data-component-id="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx:175:10" data-component-path="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx" data-component-line="175" data-component-file="CustomPlanInteractive.tsx" data-component-name="ProjectDetailsStep" data-component-content="%7B%22elementName%22%3A%22ProjectDetailsStep%22%7D"
            formData={formData}
            errors={errors}
            onInputChange={(field, value) => handleInputChange(field as keyof CustomPlanForm, value)}
          />
        );
      case 3:
        return (
          <RequirementsStep data-component-id="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx:183:10" data-component-path="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx" data-component-line="183" data-component-file="CustomPlanInteractive.tsx" data-component-name="RequirementsStep" data-component-content="%7B%22elementName%22%3A%22RequirementsStep%22%7D"
            formData={formData}
            errors={errors}
            onCheckboxChange={(field, value, checked) =>
              handleCheckboxChange(field as keyof Pick<CustomPlanForm, 'features' | 'integrations' | 'compliance'>, value, checked)
            }
            onInputChange={(field, value) => handleInputChange(field as keyof CustomPlanForm, value)}
          />
        );
      case 4:
        return (
          <DocumentUploadStep data-component-id="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx:194:10" data-component-path="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx" data-component-line="194" data-component-file="CustomPlanInteractive.tsx" data-component-name="DocumentUploadStep" data-component-content="%7B%22elementName%22%3A%22DocumentUploadStep%22%7D"
            formData={formData}
            errors={errors}
            onFileUpload={handleFileUpload}
            onFileRemove={handleFileRemove}
            onInputChange={(field, value) => handleInputChange(field as keyof CustomPlanForm, value)}
          />
        );
      default:
        return (
          <ReviewSubmitStep data-component-id="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx:204:10" data-component-path="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx" data-component-line="204" data-component-file="CustomPlanInteractive.tsx" data-component-name="ReviewSubmitStep" data-component-content="%7B%22elementName%22%3A%22ReviewSubmitStep%22%7D"
            formData={formData}
            estimatedBudget={estimatedBudgetLabel}
            estimatedTimeline={estimatedTimelineLabel}
          />
        );
    }
  };

  return (
    <div data-component-id="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx:214:4" data-component-path="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx" data-component-line="214" data-component-file="CustomPlanInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%22%7D" className="max-w-4xl mx-auto">
      <div data-component-id="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx:215:6" data-component-path="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx" data-component-line="215" data-component-file="CustomPlanInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
        <h2 data-component-id="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx:216:8" data-component-path="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx" data-component-line="216" data-component-file="CustomPlanInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20md%3Atext-4xl%20font-bold%20text-gray-900%20mb-4%22%7D" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          {t('custom_plan.title')}
        </h2>
        <p data-component-id="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx:219:8" data-component-path="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx" data-component-line="219" data-component-file="CustomPlanInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xl%20text-gray-600%22%7D" className="text-xl text-gray-600">
          {t('custom_plan.subtitle')}
        </p>
      </div>

      <FormProgressIndicator data-component-id="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx:224:6" data-component-path="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx" data-component-line="224" data-component-file="CustomPlanInteractive.tsx" data-component-name="FormProgressIndicator" data-component-content="%7B%22elementName%22%3A%22FormProgressIndicator%22%7D" steps={steps} currentStep={currentStep} />

      <div data-component-id="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx:226:6" data-component-path="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx" data-component-line="226" data-component-file="CustomPlanInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-12%20bg-white%20rounded-xl%20shadow-lg%20p-8%22%7D" className="mt-12 bg-white rounded-xl shadow-lg p-8">
        {renderStep()}
        <div data-component-id="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx:228:8" data-component-path="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx" data-component-line="228" data-component-file="CustomPlanInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-8%20flex%20justify-between%22%7D" className="mt-8 flex justify-between">
          <button data-component-id="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx:229:10" data-component-path="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx" data-component-line="229" data-component-file="CustomPlanInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22px-4%20py-2%20rounded-lg%20border%20border-border%20text-text-primary%20hover%3Aborder-accent%20disabled%3Aopacity-50%22%2C%22textContent%22%3A%22Previous%22%7D"
            type="button"
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="px-4 py-2 rounded-lg border border-border text-text-primary hover:border-accent disabled:opacity-50"
          >
            Previous
          </button>
          {currentStep < steps.length ? (
            <button data-component-id="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx:238:12" data-component-path="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx" data-component-line="238" data-component-file="CustomPlanInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22px-4%20py-2%20rounded-lg%20bg-accent%20text-white%20hover%3Abg-accent%2F90%22%2C%22textContent%22%3A%22Next%22%7D"
              type="button"
              onClick={handleNext}
              className="px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent/90"
            >
              Next
            </button>
          ) : (
            <button data-component-id="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx:246:12" data-component-path="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx" data-component-line="246" data-component-file="CustomPlanInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22px-4%20py-2%20rounded-lg%20bg-primary%20text-white%20hover%3Abg-primary%2F90%20disabled%3Aopacity-60%22%7D"
              type="button"
              onClick={handleSubmit}
              disabled={submitStatus === 'submitting'}
              className="px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-60"
            >
              {submitStatus === 'submitting' ? 'Sending...' : 'Submit'}
            </button>
          )}
        </div>
        {submitMessage && (
          <p data-component-id="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx:257:10" data-component-path="src/app/get-your-custom-plan/components/CustomPlanInteractive.tsx" data-component-line="257" data-component-file="CustomPlanInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D"
            className={`mt-4 text-sm ${
              submitStatus === 'success' ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {submitMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export default CustomPlanInteractive;
